import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { GrammarTopic, ProficiencyLevel } from '../types/grammarPractice';
import { listTopics } from '../api/grammarPracticeApi';
import { FaChevronDown, FaChevronUp, FaBookOpen, FaRandom } from 'react-icons/fa';

const GrammarLevelsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguage();

  const PROFICIENCY_LEVELS: { value: ProficiencyLevel; labelKey: string; descriptionKey: string }[] = [
    { value: 'A1', labelKey: 'proficiency.a1', descriptionKey: 'proficiency.a1Description' },
    { value: 'A2', labelKey: 'proficiency.a2', descriptionKey: 'proficiency.a2Description' },
    { value: 'B1', labelKey: 'proficiency.b1', descriptionKey: 'proficiency.b1Description' },
    { value: 'B2', labelKey: 'proficiency.b2', descriptionKey: 'proficiency.b2Description' },
    { value: 'C1', labelKey: 'proficiency.c1', descriptionKey: 'proficiency.c1Description' },
    { value: 'C2', labelKey: 'proficiency.c2', descriptionKey: 'proficiency.c2Description' },
  ];
  const [expandedLevel, setExpandedLevel] = useState<ProficiencyLevel | null>(null);
  const [topicsByLevel, setTopicsByLevel] = useState<Record<ProficiencyLevel, GrammarTopic[]>>({
    A1: [],
    A2: [],
    B1: [],
    B2: [],
    C1: [],
    C2: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedLanguage) {
      loadTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const loadTopics = async () => {
    if (!selectedLanguage) return;
    
    setLoading(true);
    setError(null);
    try {
      // Use the language code directly (e.g., 'en', 'es', 'de')
      const languageCode = selectedLanguage.iso639_tag;
      const fetchedTopics = await listTopics({ language: languageCode });
      
      // Group topics by proficiency level
      const grouped: Record<ProficiencyLevel, GrammarTopic[]> = {
        A1: [],
        A2: [],
        B1: [],
        B2: [],
        C1: [],
        C2: [],
      };
      
      fetchedTopics.forEach((topic) => {
        if (grouped[topic.proficiency_level]) {
          grouped[topic.proficiency_level].push(topic);
        }
      });
      
      setTopicsByLevel(grouped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleLevelClick = (level: ProficiencyLevel) => {
    setExpandedLevel(expandedLevel === level ? null : level);
  };

  const handleTopicSelect = (level: ProficiencyLevel, topicId?: number) => {
    // Navigate to practice page with level and optional topic
    const params = new URLSearchParams({
      level,
      ...(topicId && { topicId: topicId.toString() }),
    });
    navigate(`/grammar/practice?${params.toString()}`);
  };

  const getLevelColor = (level: ProficiencyLevel) => {
    const colors = {
      A1: 'bg-success/10 border-success',
      A2: 'bg-info/10 border-info',
      B1: 'bg-warning/10 border-warning',
      B2: 'bg-primary/10 border-primary',
      C1: 'bg-secondary/10 border-secondary',
      C2: 'bg-error/10 border-error',
    };
    return colors[level];
  };

  const getLevelBadgeColor = (level: ProficiencyLevel) => {
    const colors = {
      A1: 'badge-success',
      A2: 'badge-info',
      B1: 'badge-warning',
      B2: 'badge-primary',
      C1: 'badge-secondary',
      C2: 'badge-error',
    };
    return colors[level];
  };

  if (!selectedLanguage) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 pt-24 pb-24">
        <FaBookOpen size={64} className="text-base-content opacity-20 mb-4" />
        <p className="text-lg text-base-content opacity-60">
          {t('languageSelector.pleaseSelect')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto pt-16 pb-[7rem]">
        <div className="px-4 py-3 sticky top-0 bg-base-100/95 backdrop-blur-sm z-10 border-b border-base-300">
          {selectedLanguage && (
            <span className="text-xs text-base-content/60 truncate">
              {t('grammar.practicing', { language: selectedLanguage.language_name })}
            </span>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-4">
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Accordion List */}
        {!loading && !error && (
          <div className="p-4 space-y-3">
            {PROFICIENCY_LEVELS.map((levelInfo) => {
              const isExpanded = expandedLevel === levelInfo.value;
              const topics = topicsByLevel[levelInfo.value];
              const topicCount = topics.length;

              return (
                <div
                  key={levelInfo.value}
                  className={`border-2 rounded-lg overflow-hidden transition-all ${getLevelColor(levelInfo.value)}`}
                >
                  {/* Level Header */}
                  <button
                    onClick={() => handleLevelClick(levelInfo.value)}
                    className="w-full p-4 flex items-center justify-between hover:bg-base-200/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 text-left">
                      <span className={`badge ${getLevelBadgeColor(levelInfo.value)} badge-lg`}>
                        {levelInfo.value}
                      </span>
                      <div>
                        <h3 className="font-semibold">{t(levelInfo.labelKey)}</h3>
                        <p className="text-sm opacity-70">{t(levelInfo.descriptionKey)}</p>
                        <p className="text-xs opacity-50 mt-1">
                          {t('grammar.topicCount', { count: topicCount })}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  {/* Topics List (shown when expanded) */}
                  {isExpanded && (
                    <div className="bg-base-100 border-t-2">
                      {/* Random Topics Option */}
                      <button
                        onClick={() => handleTopicSelect(levelInfo.value)}
                        className="w-full p-4 flex items-center gap-3 hover:bg-base-200 transition-colors border-b border-base-300"
                      >
                        <FaRandom className="text-primary" />
                        <div className="text-left">
                          <h4 className="font-semibold">{t('grammar.randomTopics')}</h4>
                          <p className="text-sm opacity-70">
                            {t('grammar.randomTopicsDescription')}
                          </p>
                        </div>
                      </button>

                      {/* Individual Topics */}
                      {topics.length > 0 ? (
                        topics.map((topic) => (
                          <button
                            key={topic.grammar_topic_id}
                            onClick={() => handleTopicSelect(levelInfo.value, topic.grammar_topic_id)}
                            className="w-full p-4 flex items-start gap-3 hover:bg-base-200 transition-colors border-b border-base-300 last:border-b-0"
                          >
                            <div className="text-left flex-1">
                              <h4 className="font-semibold">{topic.name}</h4>
                              {topic.description && (
                                <p className="text-sm opacity-70 mt-1">{topic.description}</p>
                              )}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm opacity-70">
                          {t('grammar.noTopicsAvailable')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarLevelsPage;
